import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./i18n/I18nContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AppShell from "./pages/AppShell";
import Dashboard from "./pages/Dashboard";
import Listings from "./pages/Listings";
import Mieten from "./pages/Mieten";
import ListingDetail from "./pages/ListingDetail";
import Markets from "./pages/Markets";
import MarketDetail from "./pages/MarketDetail";
import Underwrite from "./pages/Underwrite";
import FixFlip from "./pages/FixFlip";
import RentCalculator from "./pages/RentCalculator";
import Analyze from "./pages/Analyze";
import Watchlist from "./pages/Watchlist";
import MarketMap from "./pages/MarketMap";
import Alerts from "./pages/Alerts";
import Diagnostics from "./pages/Diagnostics";
import Admin from "./pages/Admin";
import Settings from "./pages/Settings";
import ApiDocs from "./pages/ApiDocs";
import Briefs from "./pages/Briefs";
import Analysis from "./pages/Analysis";
import ObjectCockpit from "./pages/ObjectCockpit";
import FinancingDossier from "./pages/FinancingDossier";
import PersonalSearch from "./pages/PersonalSearch";
import Archive from "./pages/Archive";
import { Spinner } from "./components/ui";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg">
        <Spinner size={24} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Admin-only pages. The backend already gates every /admin/* endpoint with
// require_admin, so a non-admin never saw data here — but the routes were
// reachable by direct URL and rendered a wall of 403s. Send them home instead.
function AdminOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg">
        <Spinner size={24} />
      </div>
    );
  }
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

// Non-admin-configurable pages (Admin → Features tab). Same "hidden isn't
// enough on its own" reasoning as AdminOnly above — a hidden nav item must
// also not be reachable by direct URL, or it isn't really hidden.
function FeatureGated({ featureKey, children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg">
        <Spinner size={24} />
      </div>
    );
  }
  if (user?.role !== "admin" && (user?.hidden_features || []).includes(featureKey)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg">
        <Spinner size={24} />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <I18nProvider>
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/listings" element={<FeatureGated featureKey="listings"><Listings /></FeatureGated>} />
            <Route path="/mieten" element={<FeatureGated featureKey="mieten"><Mieten /></FeatureGated>} />
            <Route path="/listings/:id" element={<ListingDetail />} />
            <Route path="/markets" element={<FeatureGated featureKey="markets"><Markets /></FeatureGated>} />
            <Route path="/markets/:code" element={<MarketDetail />} />
            <Route path="/map" element={<FeatureGated featureKey="map"><MarketMap /></FeatureGated>} />
            <Route path="/alerts" element={<FeatureGated featureKey="alerts"><Alerts /></FeatureGated>} />
            <Route path="/diagnostics" element={<AdminOnly><Diagnostics /></AdminOnly>} />
            <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
            <Route path="/settings" element={<FeatureGated featureKey="settings"><Settings /></FeatureGated>} />
            <Route path="/apidocs" element={<FeatureGated featureKey="api"><ApiDocs /></FeatureGated>} />
            <Route path="/briefs" element={<FeatureGated featureKey="briefs"><Briefs /></FeatureGated>} />
            <Route path="/objekt-cockpit" element={<FeatureGated featureKey="underwriter"><ObjectCockpit /></FeatureGated>} />
            <Route path="/finanzierungsdossier" element={<FinancingDossier />} />
            <Route path="/underwrite" element={<FeatureGated featureKey="quickUnderwrite"><Underwrite /></FeatureGated>} />
            <Route path="/mietpreis" element={<FeatureGated featureKey="rentcalc"><RentCalculator /></FeatureGated>} />
            <Route path="/mietpreisvergleich" element={<RentCalculator />} />
            <Route path="/fixflip" element={<FeatureGated featureKey="fixflip"><FixFlip /></FeatureGated>} />
            <Route path="/bewerten" element={<FeatureGated featureKey="bewerter"><Analyze /></FeatureGated>} />
            <Route path="/watchlist" element={<FeatureGated featureKey="watchlist"><Watchlist /></FeatureGated>} />
            <Route path="/personal-search" element={<FeatureGated featureKey="personalSearch"><PersonalSearch /></FeatureGated>} />
            <Route path="/archive" element={<FeatureGated featureKey="archive"><Archive /></FeatureGated>} />
            <Route path="/analysis" element={<FeatureGated featureKey="analysis"><Analysis /></FeatureGated>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </I18nProvider>
  );
}
